import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle, Award, Send, Loader2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIERS = ["Aspiring", "Inspired", "Luminary"];
const SIZES = ["Micro (1-10)", "Small (11-50)", "Medium (51-250)", "Large (251-1000)", "Enterprise (1000+)"];

const EnquiriesPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialTier = params.get("tier") || "Aspiring";

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    organization_name: "",
    organization_size: "",
    industry: "",
    country: "",
    tier: initialTier,
    message: "",
    source: "certification-page",
  });

  useEffect(() => {
    setForm((f) => ({ ...f, tier: initialTier }));
  }, [initialTier]);

  const updateField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Submission failed");
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#F8FAFC] to-white" data-testid="enquiries-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-[#FDB813]" />
              <span className="text-sm font-medium text-[#1E3A5F]">Certification Enquiry</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1E3A5F] mb-6">
              Begin Your <span className="text-[#FDB813]">{form.tier}</span> Journey
            </h1>
            <p className="text-lg text-gray-600">
              Tell us about your organisation and we'll guide you through the next steps of CNESS certification.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            {submitted ? (
              <div
                className="bg-gradient-to-br from-[#FEF3C7] to-white border border-[#FDB813]/30 rounded-3xl p-12 text-center"
                data-testid="enquiry-success-card"
              >
                <div className="w-16 h-16 bg-[#FDB813] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-9 h-9 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4">Thank you!</h2>
                <p className="text-lg text-gray-700 mb-8">
                  Your enquiry for the <strong>{form.tier}</strong> tier has been received. Our certification team
                  will reach out within 1 business day.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => navigate("/certification")}
                    variant="outline"
                    className="border-2 border-[#1E3A5F] text-[#1E3A5F]"
                    data-testid="enquiry-back-cert-btn"
                  >
                    Back to Certification
                  </Button>
                  <Button
                    onClick={() => navigate("/")}
                    className="bg-[#1E3A5F] hover:bg-[#152a45] text-white"
                    data-testid="enquiry-back-home-btn"
                  >
                    Return Home <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white border border-gray-100 rounded-3xl shadow-sm p-8 sm:p-10 space-y-6"
                data-testid="enquiry-form"
              >
                {/* Tier selector */}
                <div>
                  <label className="block text-sm font-semibold text-[#1E3A5F] mb-3">
                    Certification Tier <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {TIERS.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => updateField("tier", t)}
                        data-testid={`tier-pill-${t.toLowerCase()}`}
                        className={`px-4 py-3 rounded-xl border-2 font-medium text-sm transition-all ${
                          form.tier === t
                            ? "border-[#FDB813] bg-[#FEF3C7] text-[#1E3A5F]"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Full Name" required>
                    <input
                      data-testid="enquiry-full-name"
                      required
                      value={form.full_name}
                      onChange={(e) => updateField("full_name", e.target.value)}
                      className={inputCls}
                      placeholder="Jane Doe"
                    />
                  </Field>
                  <Field label="Work Email" required>
                    <input
                      data-testid="enquiry-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      className={inputCls}
                      placeholder="jane@company.com"
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      data-testid="enquiry-phone"
                      value={form.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                      className={inputCls}
                      placeholder="+1 555 1234"
                    />
                  </Field>
                  <Field label="Country">
                    <input
                      data-testid="enquiry-country"
                      value={form.country}
                      onChange={(e) => updateField("country", e.target.value)}
                      className={inputCls}
                      placeholder="United States"
                    />
                  </Field>
                  <Field label="Organisation Name" required>
                    <input
                      data-testid="enquiry-org-name"
                      required
                      value={form.organization_name}
                      onChange={(e) => updateField("organization_name", e.target.value)}
                      className={inputCls}
                      placeholder="Acme Inc."
                    />
                  </Field>
                  <Field label="Organisation Size">
                    <select
                      data-testid="enquiry-org-size"
                      value={form.organization_size}
                      onChange={(e) => updateField("organization_size", e.target.value)}
                      className={inputCls}
                    >
                      <option value="">Select size</option>
                      {SIZES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Industry">
                      <input
                        data-testid="enquiry-industry"
                        value={form.industry}
                        onChange={(e) => updateField("industry", e.target.value)}
                        className={inputCls}
                        placeholder="e.g. Technology, Manufacturing"
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Tell us about your goals">
                      <textarea
                        data-testid="enquiry-message"
                        value={form.message}
                        onChange={(e) => updateField("message", e.target.value)}
                        className={`${inputCls} min-h-[120px] resize-y`}
                        placeholder="What are you hoping to achieve with CNESS certification?"
                      />
                    </Field>
                  </div>
                </div>

                {error && (
                  <div
                    className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm"
                    data-testid="enquiry-error"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="enquiry-submit-btn"
                  className="w-full bg-[#1E3A5F] hover:bg-[#152a45] text-white py-6 text-lg rounded-xl"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...
                    </>
                  ) : (
                    <>
                      Submit Enquiry <Send className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By submitting this form you agree to be contacted by CNESS regarding your certification enquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-12 bg-[#F8FAFC] border-t border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto text-center">
            <Trust icon={Award} title="500+ Champions" desc="Certified organisations globally" />
            <Trust icon={CheckCircle} title="97% Success Rate" desc="Of organisations complete certification" />
            <Trust icon={Sparkles} title="30 Day Average" desc="From start to certification" />
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#FDB813] focus:ring-2 focus:ring-[#FDB813]/20 text-[#1E3A5F] placeholder:text-gray-400 bg-white";

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-[#1E3A5F] mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const Trust = ({ icon: Icon, title, desc }) => (
  <div>
    <div className="w-12 h-12 bg-[#FEF3C7] rounded-xl flex items-center justify-center mx-auto mb-3">
      <Icon className="w-6 h-6 text-[#FDB813]" />
    </div>
    <h4 className="font-semibold text-[#1E3A5F] mb-1">{title}</h4>
    <p className="text-sm text-gray-500">{desc}</p>
  </div>
);

export default EnquiriesPage;
